import { inject, Injectable } from '@angular/core';
import { AuthStore } from '../auth.store';
import { CookieService } from 'ngx-cookie-service';
import { JWT, LoginPostBody, LoginResponse } from '../auth.model';
import { jwtDecode } from 'jwt-decode';
import { of, switchMap, tap } from 'rxjs';
import dayjs from 'dayjs';
import { AuthService } from '../auth.service';
import { ErrorFacade } from '../../error/error.facade';

@Injectable({ providedIn: 'root' })
export class SessionFacade {
  readonly #cookieService = inject(CookieService);
  readonly #authStore = inject(AuthStore);
  readonly #authService = inject(AuthService);
  readonly #errorFacade = inject(ErrorFacade);

  getAccessToken() {
    return this.#cookieService.get(this.#authStore.cookies().main);
  }

  getRefreshToken() {
    return this.#cookieService.get(this.#authStore.cookies().refresh);
  }

  getDecodedAccessToken(authResult?: LoginResponse): JWT | null {
    const token = authResult?.token ?? this.getAccessToken();

    if (!token) {
      return null;
    }

    try {
      return jwtDecode<JWT>(token);
    } catch (e) {
      this.#errorFacade.captureError(new Error('Error parsing token', { cause: e }));
      return null;
    }
  }

  refreshAccessToken(token: string) {
    return this.#authService
      .refreshToken(token)
      .pipe(tap((authResult: LoginResponse) => this.saveSession(authResult)));
  }

  saveSession(authResult: LoginResponse) {
    const decodedAccessToken = this.getDecodedAccessToken(authResult);

    if (decodedAccessToken) {
      this.#cookieService.set(
        this.#authStore.cookies().main,
        authResult.token,
        dayjs.unix(decodedAccessToken.exp).toDate(),
        '/',
        this.#authStore.cookieDomain()
      );

      if (authResult.refresh_token) {
        this.#cookieService.set(
          this.#authStore.cookies().refresh,
          authResult.refresh_token,
          /* TODO: ask backend for refresh token expiration time */
          dayjs().add(15, 'minute').toDate(),
          '/',
          this.#authStore.cookieDomain()
        );
      }
    }
  }

  login(body: LoginPostBody) {
    return this.#authService.login(body).pipe(
      switchMap((authResult) => {
        if (authResult) {
          this.saveSession(authResult);
          return this.#authService.getUser().pipe(tap((user) => this.#authStore.updateUser(user)));
        }

        return of(authResult);
      })
    );
  }

  logout() {
    const cookieDomain = this.#authStore.cookieDomain();
    const cookies = this.#authStore.cookies();

    this.#cookieService.delete(this.#authStore.cookies().main, '/', cookieDomain);
    this.#cookieService.delete(cookies.main, '/', cookieDomain);
    this.#cookieService.delete(cookies.refresh, '/', cookieDomain);
    this.#cookieService.delete(cookies.client, '/', cookieDomain);
    this.#authStore.updateUser(null);
  }
}
